import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  PLATFORM_ID,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';

import { StorageService } from '@ledgerly/common/browser';

import { SiteLocale } from '../nav-link';
import { localisedPath, pickSuggestion } from './pick-suggestion';

const DISMISSED_KEY = 'languageSuggestionDismissed';

/**
 * The offer, written in the language being offered.
 *
 * These deliberately do NOT go through `$localize`. `$localize` returns the
 * *current page's* language, and the whole point of this banner is to speak to
 * someone who is on the wrong page: telling a German speaker "Ledgerly is also
 * available in German" in English is exactly the sentence they cannot read.
 * So it is a table, keyed by the language being suggested.
 */
const OFFER: Record<
  string,
  { message: string; action: string; dismiss: string }
> = {
  en: {
    message: 'Ledgerly is also in English.',
    action: 'Read in English',
    dismiss: 'Dismiss',
  },
  fr: {
    message: 'Ledgerly existe aussi en français.',
    action: 'Lire en français',
    dismiss: 'Fermer',
  },
  de: {
    message: 'Ledgerly gibt es auch auf Deutsch.',
    action: 'Auf Deutsch lesen',
    dismiss: 'Schließen',
  },
  it: {
    message: 'Ledgerly è disponibile anche in italiano.',
    action: 'Leggi in italiano',
    dismiss: 'Chiudi',
  },
  es: {
    message: 'Ledgerly también está en español.',
    action: 'Leer en español',
    dismiss: 'Cerrar',
  },
};

/**
 * A quiet offer to read the site in the reader's own language.
 *
 * This replaces Firebase Hosting's language negotiation, which served one
 * locale's document at another locale's URL and left English unreachable for
 * anyone whose browser preferred French. See CLAUDE.md. Detection belongs on
 * the client, as a suggestion the reader can refuse, never as a redirect.
 *
 * Three rules it keeps:
 *
 * - It never moves anyone. It is a link, so the reader chooses, the back
 *   button works, and a crawler is unaffected.
 * - It asks once. Dismissing it is remembered, and following it means the
 *   reader is on the page they wanted, where it will not appear again.
 * - It renders nothing on the server, so the prerendered HTML of every locale
 *   is identical to what a reader with no JavaScript sees.
 *
 * On motion: this site's rule is that nothing moves that the reader did not
 * scroll to. This is the one exception, because it is a control rather than
 * decoration and appearing silently makes it easy to miss. It rises once,
 * 240ms, and never again. `prefers-reduced-motion` removes even that.
 */
@Component({
  selector: 'layout-language-suggestion',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (offer(); as suggestion) {
    <div
      class="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-8 pb-8"
      role="region"
      [attr.aria-label]="suggestion.copy.message"
    >
      <div
        class="suggestion pointer-events-auto flex w-full max-w-[520px] flex-wrap items-center gap-x-8 gap-y-4 rounded-lg border border-line bg-surface px-8 py-6 shadow-md"
      >
        <p class="text-label text-ink" [attr.lang]="suggestion.locale.code">
          {{ suggestion.copy.message }}
        </p>

        <div class="ml-auto flex items-center gap-6">
          <a
            [href]="suggestion.href"
            [attr.lang]="suggestion.locale.code"
            [attr.hreflang]="suggestion.locale.code"
            class="text-label text-accent underline decoration-accent-stroke/40 underline-offset-4"
            >{{ suggestion.copy.action }}</a
          >

          <button
            type="button"
            class="flex h-8 w-8 flex-none items-center justify-center rounded-pill text-ink-2"
            [attr.aria-label]="suggestion.copy.dismiss"
            (click)="dismiss()"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              class="h-4 w-4"
              aria-hidden="true"
            >
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
    }
  `,
  styles: [
    `
      .suggestion {
        animation: suggestion-in 240ms cubic-bezier(0.16, 1, 0.3, 1) both;
      }

      @keyframes suggestion-in {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .suggestion {
          animation: none;
        }
      }
    `,
  ],
})
export class LanguageSuggestionComponent {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storage = inject(StorageService);

  protected readonly offer = signal<{
    locale: SiteLocale;
    copy: (typeof OFFER)[string];
    href: string;
  } | null>(null);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // After render, so the decision never affects the prerendered HTML.
    afterNextRender(() => this.decide());
  }

  private decide(): void {
    const nav = this.document.defaultView?.navigator;
    if (!nav) return;

    const languages = nav.languages?.length
      ? nav.languages
      : [nav.language].filter(Boolean);

    const current = this.document.documentElement.lang || 'en';
    const dismissed = this.storage.getItem(DISMISSED_KEY) === 'true';

    const locale = pickSuggestion(languages, current, dismissed);
    if (!locale) return;

    this.offer.set({
      locale,
      copy: OFFER[locale.code],
      href: localisedPath(this.document.location.pathname, current, locale),
    });
  }

  protected dismiss(): void {
    this.storage.setItem(DISMISSED_KEY, 'true');
    this.offer.set(null);
  }
}
